import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, KeyRound, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { useTheme } from '../context/ThemeContext';

// Landing page for the "Forgot password" email. supabase-js (detectSessionInUrl)
// turns the link's token into a short-lived recovery session; this page lets the
// user choose a new password with it. Public route — must not redirect.
const ResetPassword = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [phase, setPhase] = useState('checking'); // checking | form | invalid
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let settled = false;
    const ready = () => { if (!settled) { settled = true; setPhase('form'); } };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN')) ready();
    });
    supabase.auth.getSession().then(({ data: { session } }) => { if (session) ready(); });
    // The URL token is exchanged asynchronously; give it a moment before giving up.
    const t = setTimeout(() => { if (!settled) { settled = true; setPhase('invalid'); } }, 4000);
    return () => { subscription.unsubscribe(); clearTimeout(t); };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Use at least 8 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSaving(false);
    if (error) { toast.error(error.message || 'Could not update password'); return; }
    toast.success('Password updated');
    navigate('/', { replace: true });
  };

  const card = `w-full max-w-sm rounded-xl border p-6 ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  const input = `w-full px-3.5 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#586f8d]/40 ${isDark ? 'bg-[#18181b] border-[#27272a] text-white' : 'bg-white border-[#d9d9de] text-[#1a1a1a]'}`;
  const muted = isDark ? 'text-[#a1a1aa]' : 'text-[#666]';

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${isDark ? 'bg-[#09090b]' : 'bg-[#f9f9f9]'}`}>
      <div className={card}>
        {phase === 'checking' && (
          <div className={`flex items-center gap-2 text-sm ${muted}`}><Loader2 className="h-4 w-4 animate-spin" /> Verifying your reset link…</div>
        )}
        {phase === 'invalid' && (
          <div className="text-center">
            <AlertCircle className="h-7 w-7 mx-auto mb-3 text-[#b45309]" />
            <h1 className={`text-base font-semibold mb-1.5 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Reset link invalid or expired</h1>
            <p className={`text-sm mb-4 ${muted}`}>Request a new link from the sign-in page.</p>
            <Link to="/auth" className="text-sm underline">Back to sign in</Link>
          </div>
        )}
        {phase === 'form' && (
          <form onSubmit={submit} className="space-y-4">
            <div className="flex items-center gap-2">
              <KeyRound className={`h-5 w-5 ${muted}`} />
              <h1 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Choose a new password</h1>
            </div>
            <input type="password" autoComplete="new-password" className={input} placeholder="New password" value={password} onChange={e => setPassword(e.target.value)} required />
            <input type="password" autoComplete="new-password" className={input} placeholder="Confirm new password" value={confirm} onChange={e => setConfirm(e.target.value)} required />
            <button type="submit" disabled={saving} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Update password
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
